// Кооперативы — tech/03 §7 (этап А: E07-T01, T02, T03, T05, T11, T15; вызов босса — E04-T04).
// Перевыборы, пожелания топ-3, Собрание и группа Telegram — задачами своих пачек. Числа — конфиг `coop` (ADR-0010).
import { z } from 'zod';
import { BigIdString, BossId, Grant, IsoTime, NextCursor, PublicId, Uuid7, strict } from '../common.js';
import { endpoint } from '../endpoint.js';
import { Item, Wallet } from './models.js';

export const CoopRole = z.enum(['chairman', 'deputy', 'member']);
export const JoinMode = z.enum(['open', 'request', 'closed']);
/** Постройки кооператива (ГД 07 §6.2). */
export const CoopBuildingId = z.enum(['c_shop', 'c_guard', 'c_corner', 'c_board', 'c_boiler', 'c_yard', 'c_gate']);

const CoopIdParams = strict({ coopId: BigIdString });

/** Карточка в поиске (03 §7 `GET /coops`). */
export const CoopCard = strict({
  coopId: BigIdString,
  name: z.string().min(3).max(24),
  tag: z.string().min(2).max(5).nullable(),
  emblem: z.string().max(32),
  level: z.int().min(1).max(50),
  membersCount: z.int().nonnegative(),
  capacity: z.int().positive(),
  joinMode: JoinMode,
  minLevel: z.int().min(1).max(100),
  activity: z.enum(['normal', 'dormant']),
  /** «Соседский кооператив» с председателем-персонажем (ГД 07 §11). */
  npcChairman: z.boolean(),
});

export const CoopMember = strict({
  publicId: PublicId,
  displayName: z.string().min(1).max(32),
  level: z.int().min(1).max(100),
  role: CoopRole,
  joinedAt: IsoTime,
  /** Отложенное исключение (стаж 7+ дней, ГД 07 §9). */
  kickEffectiveAt: IsoTime.nullable(),
  // Заявка фронтенда-1 (Р-87): необязательны в схеме ради моков клиента; сервер отдаёт всегда.
  /** Очки Собрания за текущую неделю (ГД 07 §8). */
  weekPts: z.int().nonnegative().optional(),
  lastSeenAt: IsoTime.nullable().optional(),
});

/** Кооператив (03 §7 `GET /coops/{coopId}`): участники, роли, стройка, склад кратко, роспуск. */
export const Coop = CoopCard.extend({
  description: z.string().max(300).nullable(),
  tgGroupUrl: z.string().nullable(),
  status: z.enum(['active', 'disbanding', 'frozen', 'disbanded']),
  buildPts: z.int().nonnegative(),
  /** Очков стройки до следующего уровня; null — потолок. */
  buildPtsToNext: z.int().nonnegative().nullable(),
  plansAvailable: z.int().nonnegative(),
  summons: z.int().min(0).max(3),
  members: z.array(CoopMember),
  myRole: CoopRole.nullable(),
  active7d: z.int().nonnegative(),
  disbandAt: IsoTime.nullable(),
  disbandVote: strict({
    endsAt: IsoTime, deputies: z.int().positive(), against: z.int().nonnegative(), needed: z.int().positive(),
    myVote: z.enum(['against', 'not_against']).nullable().optional(),
  }).nullable(),
  /** Закреплённое сообщение (`SCR-COOP-SETTINGS`, ux/07 §13; Р-87). */
  pinnedMessage: z.string().max(300).nullable().optional(),
  /** Когда можно переименовать снова (раз в 30 дней, ГД 07 §7); null — уже можно. Р-87. */
  nextRenameAt: IsoTime.nullable().optional(),
  /** Заявки на вступление — только председателю и заместителям. */
  requests: z.array(strict({ reqId: BigIdString, publicId: PublicId, displayName: z.string(), level: z.int(), message: z.string().max(200).nullable(), createdAt: IsoTime })).optional(),
});
export type Coop = z.infer<typeof Coop>;

export const listCoops = endpoint({
  id: 'coop.list', method: 'GET', path: '/coops', auth: true, signed: false, idempotency: 'read', rate: 'read',
  query: strict({
    q: z.string().max(24).optional(), joinMode: JoinMode.optional(), minLevelLe: z.coerce.number().int().optional(),
    hasSlots: z.enum(['0', '1']).optional(), recommend: z.enum(['0', '1']).optional(),
    cursor: z.string().max(256).optional(), limit: z.coerce.number().int().min(1).max(50).optional(),
  }),
  response: strict({ items: z.array(CoopCard), nextCursor: NextCursor }), status: 200, errors: [], spec: 'tech/03 §7; ГД 07 §4', task: 'E07-T01',
});

export const createCoop = endpoint({
  id: 'coop.create', method: 'POST', path: '/coops', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: strict({
    name: z.string().min(3).max(24), tag: z.string().min(2).max(5).optional(), emblem: z.string().max(32).optional(),
    joinMode: JoinMode, minLevel: z.int().min(1).max(30), description: z.string().max(300).optional(),
  }),
  response: Coop, status: 201, errors: ['NAME_TAKEN', 'ALREADY_IN_COOP', 'NOT_ENOUGH_NUTS', 'LEVEL_TOO_LOW', 'NAME_INVALID'],
  spec: 'tech/03 §7; ГД 07 §2', task: 'E07-T01',
});

export const getCoop = endpoint({
  id: 'coop.get', method: 'GET', path: '/coops/{coopId}', auth: true, signed: false, idempotency: 'read', rate: 'read',
  params: CoopIdParams, response: Coop, status: 200, errors: ['NOT_FOUND'], spec: 'tech/03 §7', task: 'E07-T01',
});

/**
 * Настройки (tech/03 §7, ux/07 §13; заявка фронтенда-1, Р-87). Название и эмблема — только председатель: название раз в
 * 30 дней за гайки (`coop.num.renamePriceNuts`), смена эмблемы — за талоны (economy/06 §4, по цене товара `GOOD-COOP-EMBLEM` из `shop.catalog`);
 * остальное — председатель или заместитель. COOLDOWN: `details.availableAt`.
 */
export const updateCoop = endpoint({
  id: 'coop.update', method: 'PATCH', path: '/coops/{coopId}', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  params: CoopIdParams,
  body: strict({
    name: z.string().min(3).max(24).optional(), emblem: z.string().max(32).optional(), joinMode: JoinMode.optional(),
    minLevel: z.int().min(1).max(30).optional(), description: z.string().max(300).nullable().optional(), pinnedMessage: z.string().max(300).nullable().optional(),
  }),
  response: Coop, status: 200,
  errors: ['FORBIDDEN_ROLE', 'COOP_FROZEN', 'NAME_TAKEN', 'NAME_INVALID', 'COOLDOWN', 'NOT_ENOUGH_NUTS', 'NOT_ENOUGH_TALONS', 'TALONS_DEBT', 'NOT_IN_COOP'],
  spec: 'tech/03 §7; ГД 07 §7; ux/07 §13', task: 'E07-T02',
});

export const joinCoop = endpoint({
  id: 'coop.join', method: 'POST', path: '/coops/{coopId}/join', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: CoopIdParams, body: strict({ message: z.string().max(200).optional(), inviteToken: z.string().max(64).optional() }),
  response: strict({ status: z.enum(['joined', 'requested']) }), status: 200,
  errors: ['COOP_CLOSED', 'ALREADY_IN_COOP', 'COOP_FULL', 'REQUESTS_LIMIT', 'LEVEL_TOO_LOW', 'REJOIN_COOLDOWN'], spec: 'tech/03 §7; ГД 07 §4', task: 'E07-T01',
});

export const decideRequest = endpoint({
  id: 'coop.request.decide', method: 'POST', path: '/coops/{coopId}/requests/{reqId}/decide', auth: true, signed: true, idempotency: 'natural', rate: 'write',
  params: strict({ coopId: BigIdString, reqId: BigIdString }), body: strict({ accept: z.boolean() }),
  response: strict({ status: z.enum(['accepted', 'declined', 'cancelled', 'expired']) }), status: 200,
  errors: ['FORBIDDEN_ROLE', 'COOP_FULL', 'NOT_FOUND'], spec: 'tech/03 §7', task: 'E07-T01',
});

export const leaveCoop = endpoint({
  id: 'coop.leave', method: 'POST', path: '/coops/{coopId}/leave', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: CoopIdParams, body: strict({}), response: strict({ ok: z.literal(true), rejoinAt: IsoTime.nullable() }), status: 200,
  errors: ['CHAIRMAN_MUST_TRANSFER', 'NOT_IN_COOP'], spec: 'tech/03 §7; ГД 07 §9.2', task: 'E07-T02',
});

export const DisbandVote = strict({ endsAt: IsoTime, deputies: z.int().positive(), against: z.int().nonnegative(), needed: z.int().positive() });

/** Роспуск председателем: 48 ч с окном голосования заместителей (ВР-75); один в кооперативе — сразу. */
export const dissolveCoop = endpoint({
  id: 'coop.dissolve', method: 'POST', path: '/coops/{coopId}/dissolve', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  params: CoopIdParams, body: strict({ confirm: z.literal(true) }),
  response: z.union([
    strict({ status: z.literal('disbanding'), disbandAt: IsoTime, vote: DisbandVote.nullable() }),
    strict({ status: z.literal('disbanded') }),
  ]),
  status: 202, errors: ['FORBIDDEN_ROLE', 'CONFLICT', 'COOLDOWN', 'ELECTION_ACTIVE', 'COOP_FROZEN'], spec: 'tech/03 §7; ГД 07 §9; ВР-75', task: 'E07-T02',
});

export const cancelDissolve = endpoint({
  id: 'coop.dissolve.cancel', method: 'POST', path: '/coops/{coopId}/dissolve/cancel', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: CoopIdParams, body: strict({}), response: strict({ status: z.literal('active') }), status: 200,
  errors: ['FORBIDDEN_ROLE', 'NOT_SCHEDULED'], spec: 'tech/03 §7; ВР-75', task: 'E07-T02',
});

export const voteDissolve = endpoint({
  id: 'coop.dissolve.vote', method: 'POST', path: '/coops/{coopId}/dissolve/vote', auth: true, signed: true, idempotency: 'key', rate: 'write',
  params: CoopIdParams, body: strict({ against: z.boolean() }),
  response: strict({ status: z.enum(['disbanding', 'active']), vote: DisbandVote, myVote: z.enum(['against', 'not_against']) }), status: 200,
  errors: ['FORBIDDEN_ROLE', 'CONFLICT'], spec: 'tech/03 §7; ВР-75', task: 'E07-T02',
});

export const inviteToCoop = endpoint({
  id: 'coop.invite', method: 'POST', path: '/coops/{coopId}/invites', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: CoopIdParams, body: strict({ publicId: PublicId }), response: strict({ inviteId: BigIdString, expiresAt: IsoTime }), status: 200,
  errors: ['FORBIDDEN_ROLE', 'ALREADY_IN_COOP', 'COOP_FULL', 'REQUESTS_LIMIT', 'NOT_FOUND'], spec: 'tech/03 §7; ГД 07 §4', task: 'E07-T01',
});

export const getMyCoopInvites = endpoint({
  id: 'coop.invites.mine', method: 'GET', path: '/me/coop-invites', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: strict({ items: z.array(strict({ inviteId: BigIdString, coop: CoopCard, invitedBy: strict({ publicId: PublicId, displayName: z.string() }), expiresAt: IsoTime })) }),
  status: 200, errors: [], spec: 'tech/03 §7', task: 'E07-T01',
});

/** Мои заявки на вступление (ожидающие решения; Р-87). */
export const getMyCoopRequests = endpoint({
  id: 'coop.requests.mine', method: 'GET', path: '/me/coop-requests', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: strict({ items: z.array(strict({ reqId: BigIdString, coop: CoopCard, createdAt: IsoTime, expiresAt: IsoTime })) }),
  status: 200, errors: [], spec: 'tech/03 §7; ГД 07 §4', task: 'E07-T01',
});

/** Отозвать свою заявку (Р-87). */
export const withdrawCoopRequest = endpoint({
  id: 'coop.request.withdraw', method: 'DELETE', path: '/coops/{coopId}/requests/{reqId}', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: strict({ coopId: BigIdString, reqId: BigIdString }), response: strict({ status: z.literal('cancelled') }), status: 200,
  errors: ['NOT_FOUND'], spec: 'tech/03 §7; ГД 07 §4', task: 'E07-T01',
});

export const decideInvite = endpoint({
  id: 'coop.invite.decide', method: 'POST', path: '/me/coop-invites/{inviteId}/decide', auth: true, signed: true, idempotency: 'natural', rate: 'write',
  params: strict({ inviteId: BigIdString }), body: strict({ accept: z.boolean() }),
  response: strict({ status: z.enum(['accepted', 'declined']) }), status: 200,
  errors: ['COOP_FULL', 'ALREADY_IN_COOP', 'GONE', 'REJOIN_COOLDOWN'], spec: 'tech/03 §7', task: 'E07-T01',
});

const MemberParams = strict({ coopId: BigIdString, publicId: PublicId });

/** Роль участника (03 §7 `…/members/{playerId}/role`): в пути — публичный ID игрока (Т-К-24, Р-54). */
export const setCoopRole = endpoint({
  id: 'coop.member.role', method: 'POST', path: '/coops/{coopId}/members/{publicId}/role', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: MemberParams, body: strict({ role: CoopRole }), response: Coop, status: 200,
  errors: ['FORBIDDEN_ROLE', 'COOP_FROZEN', 'ELECTION_ACTIVE', 'DISBAND_SCHEDULED', 'REQUIREMENT_NOT_MET', 'LIMIT_REACHED', 'COOLDOWN'], spec: 'tech/03 §7; ГД 07 §3', task: 'E07-T02',
});

export const kickMember = endpoint({
  id: 'coop.member.kick', method: 'POST', path: '/coops/{coopId}/members/{publicId}/kick', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: MemberParams, body: strict({ reason: z.string().max(200).optional() }),
  response: strict({ ok: z.literal(true), effectiveAt: IsoTime }), status: 200,
  errors: ['FORBIDDEN_ROLE', 'DAILY_LIMIT', 'COOP_FROZEN', 'ELECTION_ACTIVE', 'DISBAND_SCHEDULED', 'NOT_FOUND'], spec: 'tech/03 §7; ГД 07 §3, §9', task: 'E07-T02',
});

export const cancelKick = endpoint({
  id: 'coop.member.kick.cancel', method: 'DELETE', path: '/coops/{coopId}/members/{publicId}/kick', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: MemberParams, response: strict({ ok: z.literal(true) }), status: 200,
  errors: ['FORBIDDEN_ROLE', 'COOP_FROZEN', 'NOT_FOUND'], spec: 'tech/03 §7; ГД 07 §9', task: 'E07-T02',
});

export const CoopBuilding = strict({
  buildingId: CoopBuildingId, level: z.int().min(0).max(5), requiredCoopLevel: z.int().positive().nullable(), bonus: z.record(z.string(), z.number()),
});

export const getCoopBuildings = endpoint({
  id: 'coop.buildings', method: 'GET', path: '/coops/{coopId}/buildings', auth: true, signed: false, idempotency: 'read', rate: 'read',
  params: CoopIdParams, response: strict({ plansAvailable: z.int().nonnegative(), buildings: z.array(CoopBuilding) }), status: 200,
  errors: ['NOT_IN_COOP'], spec: 'tech/03 §7; ГД 07 §6.2', task: 'E07-T03',
});

export const upgradeCoopBuilding = endpoint({
  id: 'coop.building.upgrade', method: 'POST', path: '/coops/{coopId}/buildings/{buildingId}/upgrade', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  params: strict({ coopId: BigIdString, buildingId: CoopBuildingId }), body: strict({}),
  response: strict({ building: CoopBuilding, plansAvailable: z.int().nonnegative() }), status: 200,
  errors: ['FORBIDDEN_ROLE', 'NO_PLANS', 'COOP_LEVEL_TOO_LOW', 'BUILDING_MAX'], spec: 'tech/03 §7; ГД 07 §6.2', task: 'E07-T03',
});

export const StorageLogItem = strict({ publicId: PublicId, displayName: z.string(), currency: z.enum(['nuts', 'scrap']), amount: z.int().positive(), countedPts: z.int().nonnegative(), at: IsoTime });

export const getCoopStorage = endpoint({
  id: 'coop.storage', method: 'GET', path: '/coops/{coopId}/storage', auth: true, signed: false, idempotency: 'read', rate: 'read',
  params: CoopIdParams, response: strict({ buildPts: z.int().nonnegative(), summons: z.int().min(0).max(3), log: z.array(StorageLogItem) }), status: 200,
  errors: ['NOT_IN_COOP'], spec: 'tech/03 §7; ГД 07 §6.1', task: 'E07-T03',
});

/** Сдача в склад-копилку: гайки и лом → очки стройки; из склада игрокам не выдаётся ничего (ГД 07 §6.1, A20). */
export const depositCoop = endpoint({
  id: 'coop.storage.deposit', method: 'POST', path: '/coops/{coopId}/storage/deposit', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  params: CoopIdParams, body: strict({ currency: z.enum(['nuts', 'scrap']), amount: z.int().positive() }),
  response: strict({
    buildPts: z.int().nonnegative(), wallet: Wallet, buildPtsAdded: z.int().nonnegative(), countedPts: z.int().nonnegative(),
    dailyCapLeft: z.int().nonnegative(), coopLevel: z.int().min(1).max(50), myWeekPoints: z.int().nonnegative(), marksAdded: z.int().nonnegative(),
  }),
  status: 200, errors: ['NOT_IN_COOP', 'NOT_ENOUGH_NUTS', 'NOT_ENOUGH_SCRAP', 'STORAGE_FULL'], spec: 'tech/03 §7; economy/01 §3.8', task: 'E07-T03',
});

export const doChore = endpoint({
  id: 'coop.chore', method: 'POST', path: '/coops/{coopId}/chores', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  params: CoopIdParams, body: strict({ choreId: z.string().regex(/^[a-z_]{2,32}$/) }),
  response: strict({ rewards: Grant, buildPtsAdded: z.int().nonnegative(), wallet: Wallet, choresLeftToday: z.int().nonnegative() }), status: 200,
  errors: ['NOT_IN_COOP', 'NOT_ENOUGH_ENERGY', 'DAILY_LIMIT', 'TENURE_TOO_SHORT'], spec: 'tech/03 §7; ГД 07 §6.3', task: 'E07-T03',
});

export const getCoopShop = endpoint({
  id: 'coop.shop', method: 'GET', path: '/coops/{coopId}/shop', auth: true, signed: false, idempotency: 'read', rate: 'read',
  params: CoopIdParams,
  response: strict({
    items: z.array(strict({ itemId: z.string().max(32), priceMarks: z.int().positive(), grant: Grant, limit: strict({ left: z.int().nonnegative(), total: z.int().positive(), period: z.enum(['week', 'season', 'lifetime']) }).optional() })),
    marks: z.int().nonnegative(), marksCap: z.int().positive(),
  }),
  status: 200, errors: ['NOT_IN_COOP'], spec: 'tech/03 §7; ГД 07 §6.4; economy/06 §4.1', task: 'E07-T11',
});

export const buyCoopShop = endpoint({
  id: 'coop.shop.buy', method: 'POST', path: '/coops/{coopId}/shop/buy', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  params: CoopIdParams, body: strict({ itemId: z.string().max(32), qty: z.int().min(1).max(10) }),
  response: strict({ grants: Grant, wallet: Wallet }), status: 200,
  errors: ['NOT_IN_COOP', 'NOT_ENOUGH_MARKS', 'WEEKLY_LIMIT', 'LIMIT_REACHED', 'NOT_FOUND'], spec: 'tech/03 §7; ГД 07 §6.4', task: 'E07-T11',
});

/** Общие задания Доски объявлений (ГД 09 §4.2, А2-008). */
export const getCoopBoard = endpoint({
  id: 'coop.board', method: 'GET', path: '/coops/{coopId}/board', auth: true, signed: false, idempotency: 'read', rate: 'read',
  params: CoopIdParams,
  response: strict({
    dayKey: z.string(), resetsAt: IsoTime, nAct: z.int().nonnegative(),
    tasks: z.array(strict({
      taskId: z.int().min(1).max(5), defId: z.string().regex(/^Q-C-[0-9]{2}$/), category: z.string(), progress: z.int().nonnegative(), target: z.int().positive(),
      status: z.enum(['active', 'completed', 'replaced', 'expired']), completedAt: IsoTime.nullable(),
      rewards: strict({ member: Grant, coopBuildPts: z.int().nonnegative() }), mySteps: z.int().nonnegative(), myEligible: z.boolean(),
    })),
  }),
  status: 200, errors: ['NOT_IN_COOP'], spec: 'tech/03 §7; ГД 09 §4.2', task: 'E07-T15',
});

/** Кооперативный инстанс босса (03 §7 `boss-calls`, tech/04 §11; ГД 07 §7). */
export const BossInstanceView = strict({
  iid: Uuid7, bossId: BossId, hp: z.int().nonnegative(), hpMax: z.int().positive(), status: z.enum(['active', 'defeated', 'expired', 'finalized']),
  expiresAt: IsoTime, participants: z.int().nonnegative(),
});

export const callCoopBoss = endpoint({
  id: 'coop.boss.call', method: 'POST', path: '/coops/{coopId}/boss-calls', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  params: CoopIdParams, body: strict({ bossId: BossId }), response: strict({ instance: BossInstanceView }), status: 201,
  errors: ['FORBIDDEN_ROLE', 'INSTANCE_ACTIVE', 'NO_SUMMONS', 'BOSS_LOCKED', 'BOSS_NOT_ELIGIBLE'], spec: 'tech/03 §7; ГД 07 §7; tech/04 §11', task: 'E04-T04',
});

/** Инстанс босса для `MOD-BOSS` (03 §6 `GET /bosses/{bossId}/instance?mode=coop|world`); у solo инстанса нет. */
export const getBossInstance = endpoint({
  id: 'boss.instance', method: 'GET', path: '/bosses/{bossId}/instance', auth: true, signed: false, idempotency: 'read', rate: 'read',
  params: strict({ bossId: BossId }), query: strict({ mode: z.enum(['coop', 'world']) }),
  response: BossInstanceView.extend({
    top: z.array(strict({ publicId: PublicId, displayName: z.string(), damage: z.int().nonnegative() })).max(10),
    me: strict({ damage: z.int().nonnegative(), qualified: z.boolean(), admitted: z.boolean() }),
  }),
  status: 200, errors: ['NOT_FOUND'], spec: 'tech/03 §6; ГД 05 §11', task: 'E03-T03',
});

/**
 * Итог общего инстанса для `MOD-INSTANCE-RESULT` (ux/04 §3; заявка фронтенда-2 E04-T07, Р-89): награда уже начислена при
 * финализации (Base + доля котла, письмо — уведомлением), здесь — показ. Участникам и участникам кооператива-владельца;
 * до финализации — 409 CONFLICT.
 */
export const getInstanceResult = endpoint({
  id: 'boss.instance.result', method: 'GET', path: '/bosses/instances/{iid}/result', auth: true, signed: false, idempotency: 'read', rate: 'read',
  params: strict({ iid: Uuid7 }),
  response: strict({
    iid: Uuid7, bossId: BossId, outcome: z.enum(['defeated', 'expired']),
    /** Снято HP, % с точностью 0.1; «сгорел и снято < 50%» — награды нет. */
    hpRemovedPct: z.number().min(0).max(100),
    /** Последний удар — только отметка, без ценности (ux/04 §3); у сгоревшего — null. */
    lastHit: strict({ publicId: PublicId, displayName: z.string() }).nullable(),
    me: strict({ damage: z.int().nonnegative(), qualified: z.boolean(), admitted: z.boolean() }),
    reward: Grant.nullable(),
    pot: strict({ chancePct: z.number().min(0).max(100), item: Item.nullable() }),
    mailed: z.boolean(),
    seen: z.boolean(),
  }),
  status: 200, errors: ['NOT_FOUND', 'CONFLICT'], spec: 'tech/03 §6; ux/04 §3; ГД 05 §12', task: 'E04-T07',
});

export const ackInstanceResult = endpoint({
  id: 'boss.instance.result.ack', method: 'POST', path: '/bosses/instances/{iid}/result/ack', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: strict({ iid: Uuid7 }), body: strict({}), response: strict({ ok: z.literal(true) }), status: 200,
  errors: ['NOT_FOUND', 'CONFLICT'], spec: 'tech/03 §6; ux/04 §3', task: 'E04-T07',
});

/**
 * Лист `MOD-COOP-BOSS-CALL` (ux/07 §6; Р-89): объявления о сборе, активный инстанс и кандидаты B01–B12 с числом
 * победивших в одиночку. Цена вызова — 1 объявление; очков стройки ГД 07 §7 не берёт: `costBuildPts` всегда 0.
 */
export const getCoopBossCalls = endpoint({
  id: 'coop.boss.calls', method: 'GET', path: '/coops/{coopId}/boss-calls', auth: true, signed: false, idempotency: 'read', rate: 'read',
  params: CoopIdParams,
  response: strict({
    summons: z.int().min(0).max(3),
    summonsMax: z.int().positive(),
    buildPts: z.int().nonnegative(),
    active: BossInstanceView.nullable(),
    candidates: z.array(strict({
      bossId: BossId,
      winners: z.int().nonnegative(),
      need: z.int().positive(),
      /** HP инстанса, если вызвать сейчас; null — победивших ещё нет. */
      hpMax: z.int().positive().nullable(),
      costBuildPts: z.int().nonnegative(),
      /** Можно вызвать: победивших не меньше `need` и босс открыт самому смотрящему (X-08). */
      eligible: z.boolean(),
      lockedForMe: z.boolean(),
    })),
  }),
  status: 200, errors: ['NOT_IN_COOP'], spec: 'tech/03 §7; ГД 07 §7; ux/07 §6', task: 'E04-T07',
});

/** Вклады в инстанс (03 §6, `MOD-CONTRIBUTION`). */
export const getInstanceContributions = endpoint({
  id: 'boss.instance.contributions', method: 'GET', path: '/bosses/instances/{iid}/contributions', auth: true, signed: false, idempotency: 'read', rate: 'read',
  params: strict({ iid: Uuid7 }), query: strict({ limit: z.coerce.number().int().min(1).max(50).optional() }),
  response: strict({
    top: z.array(strict({ rank: z.int().positive(), publicId: PublicId, displayName: z.string(), damage: z.int().nonnegative() })),
    me: strict({ rank: z.int().positive().nullable(), damage: z.int().nonnegative(), qualified: z.boolean() }),
  }),
  status: 200, errors: ['NOT_FOUND'], spec: 'tech/03 §6; ADR-0020', task: 'E03-T03',
});

/** Группа Telegram кооператива (tech/03 §7, ux/07 §12; E08-T05): только `t.me/+…` и `t.me/joinchat/…`. */
export const setTgGroup = endpoint({
  id: 'coop.tg_group.set', method: 'POST', path: '/coops/{coopId}/tg-group', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: CoopIdParams, body: strict({ url: z.string().max(200) }), response: strict({ tgGroupUrl: z.string() }), status: 200,
  errors: ['FORBIDDEN_ROLE', 'URL_INVALID', 'NOT_IN_COOP'], spec: 'tech/03 §7; ADR-0028 §6', task: 'E08-T05',
});

export const deleteTgGroup = endpoint({
  id: 'coop.tg_group.delete', method: 'DELETE', path: '/coops/{coopId}/tg-group', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: CoopIdParams, response: strict({ tgGroupUrl: z.null() }), status: 200, errors: ['FORBIDDEN_ROLE', 'NOT_FOUND', 'NOT_IN_COOP'], spec: 'tech/03 §7', task: 'E08-T05',
});
